import { useState, useEffect, useRef, useMemo } from 'react';
import { useLanguage, langPaths } from '@/lib/i18n';
import { usePageMeta, seoData } from '@/lib/seo';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import ContactForm from '@/components/ContactForm';
import { Play, ArrowRight, Quote, X } from 'lucide-react';
import Autoplay from 'embla-carousel-autoplay';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_6c9f827d-2b98-4123-8d21-968baa0ee9cb/artifacts/o9dp6cfp_hero-avion-skyboard.png';
const KPI_IMG = 'https://customer-assets.emergentagent.com/job_6c9f827d-2b98-4123-8d21-968baa0ee9cb/artifacts/92iq24ss_panel-kpis-skyboard.png';
const YOUTUBE_VIDEO_ID = 'rSjwIBBoFI0';

const FEATURE_IMAGES = [
  'https://customer-assets.emergentagent.com/job_skyboard-preview-1/artifacts/hgilfm4f_Captura%20de%20pantalla%202026-04-20%20a%20las%2016.10.40.png',
  KPI_IMG,
  'https://customer-assets.emergentagent.com/job_skyboard-preview-1/artifacts/u1rcbbqy_Captura%20de%20pantalla%202026-04-20%20a%20las%2016.15.27.png',
  'https://images.unsplash.com/photo-1758691736424-4b4273948341?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN0cmF0ZWd5JTIwbWVldGluZ3xlbnwwfHx8fDE3NzU5ODkyOTR8MA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxleGVjdXRpdmUlMjBkZWNpc2lvbnMlMjBkYXRhJTIwYW5hbHl0aWNzfGVufDB8fHx8MTc3NTk4OTI5NHww&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1758691736483-5f600b509962?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxleGVjdXRpdmUlMjBkZWNpc2lvbnMlMjBkYXRhJTIwYW5hbHl0aWNzfGVufDB8fHx8MTc3NTk4OTI5NHww&ixlib=rb-4.1.0&q=85',
];

const ANIMATION_FRAMES = 125; // ~2s at 60fps (125 frames * 16ms)
const STAR_COUNT = 60;
const SHOOTING_STAR_COUNT = 3;
const SHOOTING_STAR_SPACING = 15; // percentage spacing between shooting stars

function CountUp({ end, suffix = '%' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const animated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true;
          let start = 0;
          const step = end / ANIMATION_FRAMES;
          const timer = setInterval(() => {
            start += step;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [end]); // ref, animated are stable refs; IntersectionObserver is a browser global

  return <span ref={ref}>{count}{suffix}</span>;
}

function useInView() {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // runs once on mount; ref is stable, IntersectionObserver is a browser global
  return [ref, inView];
}

function StarField() {
  const stars = useMemo(() =>
    Array.from({ length: STAR_COUNT }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: Math.random() * 2 + 1,
      duration: `${Math.random() * 3 + 2}s`,
      delay: `${Math.random() * 4}s`,
      opacity: Math.random() * 0.6 + 0.2,
    })), []); // eslint-disable-line react-hooks/exhaustive-deps -- static random data generated once on mount

  const shootingStars = useMemo(() =>
    Array.from({ length: 3 }, (_, i) => ({
      id: `shoot-${i}`,
      top: `${5 + i * 15 + Math.random() * 10}%`,
      left: `${5 + Math.random() * 40}%`,
      delay: `${i * 0.7}s`,
    })), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="stars-container">
      {stars.map((s) => (
        <div
          key={s.id}
          className="star"
          style={{
            left: s.left,
            top: s.top,
            width: `${s.size}px`,
            height: `${s.size}px`,
            '--duration': s.duration,
            '--max-opacity': s.opacity,
            animationDelay: s.delay,
          }}
        />
      ))}
      {shootingStars.map((s) => (
        <div
          key={s.id}
          className="shooting-star"
          style={{
            top: s.top,
            left: s.left,
            '--shoot-delay': s.delay,
          }}
        />
      ))}
    </div>
  );
}

function TestimonialsSection({ t, carouselOpts, carouselPlugins }) {
  return (
    <section className="py-12 sm:py-20" data-testid="testimonials-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white text-center mb-12 heading-accent">{t.testimonials.title}</h2>
        <div className="px-12">
          <Carousel opts={carouselOpts} plugins={carouselPlugins} className="w-full">
            <CarouselContent>
              {t.testimonials.items.map((item) => (
                <CarouselItem key={item.name}>
                  <div className="glass rounded-2xl p-8 sm:p-10 text-center" data-testid={`testimonial-${item.name}`}>
                    <Quote className="h-10 w-10 text-[#1a6cf5]/30 mx-auto mb-6" />
                    <p className="text-base sm:text-lg text-gray-200 italic leading-relaxed mb-8">
                      &ldquo;{item.quote}&rdquo;
                    </p>
                    <p className="text-white font-semibold">{item.name}</p>
                    <p className="text-xs text-[#1a6cf5] uppercase tracking-wider mt-2">{item.role}</p>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="text-white border-white/20 hover:bg-white/10 bg-[#121b33]/80" data-testid="carousel-prev" />
            <CarouselNext className="text-white border-white/20 hover:bg-white/10 bg-[#121b33]/80" data-testid="carousel-next" />
          </Carousel>
        </div>
      </div>
    </section>
  );
}

function MetricsSection({ t }) {
  return (
    <section className="py-10 sm:py-16" data-testid="metrics-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-5xl mx-auto border border-white/5 rounded-2xl bg-[#121b33]/30 py-12 px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {t.metrics.items.map((m) => (
              <div key={m.label} className="text-center group" data-testid={`metric-${m.value}`}>
                <div className="font-heading text-5xl sm:text-6xl font-bold text-shimmer group-hover:text-[#3b82f6] transition-colors duration-300">
                  <CountUp end={m.value} />
                </div>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FaqSection({ t }) {
  return (
    <section className="py-12 sm:py-20" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white text-center mb-10 heading-accent">{t.faq.title}</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {t.faq.items.map((item, i) => (
            <AccordionItem
              key={`faq-${item.q.slice(0, 20)}`}
              value={`faq-${i}`}
              className="border border-white/10 rounded-xl px-6 bg-[#121b33]/30 data-[state=open]:border-[#1a6cf5]/30 transition-colors"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="text-white text-left font-medium py-5 hover:no-underline hover:text-[#1a6cf5] transition-colors">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-gray-400 leading-relaxed pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function VideoModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="video-modal-overlay"
    >
      <div className="relative w-full max-w-4xl mx-4" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors" data-testid="video-modal-close">
          <X className="h-8 w-8" />
        </button>
        <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl shadow-[#1a6cf5]/20" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0`}
            title="Skyboard Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            data-testid="video-player"
          />
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t, lang } = useLanguage();
  const seo = seoData[lang].home;
  usePageMeta({ title: seo.title, description: seo.description, path: langPaths[lang], lang });
  const [featuresRef, featuresInView] = useInView();
  const [labRef, labInView] = useInView();
  const [videoOpen, setVideoOpen] = useState(false);

  const carouselOpts = useMemo(() => ({ loop: true }), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const carouselPlugins = useMemo(() => [Autoplay({ delay: 5000, stopOnInteraction: true })], []); // Autoplay is a stable module-level import

  return (
    <main className="pt-16" data-testid="home-page">
      {/* Hero */}
      <section className="py-14 sm:py-20 relative overflow-hidden" data-testid="hero-section">
        <StarField />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1 space-y-6 text-center lg:text-left">
              <p className="text-sm uppercase tracking-[0.2em] text-[#1a6cf5] font-medium">
                {t.hero.tagline}
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
                {t.hero.h1_1}<br />{t.hero.h1_2}
              </h1>
              <p className="text-base sm:text-lg text-gray-300 max-w-xl mx-auto lg:mx-0">
                {t.hero.subtitle}
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <a
                  href="#features"
                  className="bg-[#1a6cf5] text-white rounded-full px-8 py-3 font-medium hover:bg-[#3b82f6] transition-all btn-glow"
                  data-testid="hero-explore-btn"
                >
                  {t.hero.cta_explore}
                </a>
                <button
                  onClick={() => setVideoOpen(true)}
                  className="bg-gray-800 text-white rounded-full px-8 py-3 font-medium hover:bg-gray-700 border border-gray-700 flex items-center gap-2 transition-all cursor-pointer"
                  data-testid="hero-video-btn"
                >
                  <Play className="h-4 w-4" /> {t.hero.cta_video}
                </button>
              </div>
              <a
                href="https://app.skyboard.es"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#1a6cf5] hover:text-[#3b82f6] font-medium transition-colors"
                data-testid="hero-simulator-link"
              >
                {t.hero.cta_simulator} <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <div className="flex-1 max-w-lg lg:max-w-none">
              <img
                src={HERO_IMG}
                alt="Skyboard Airplane Simulation"
                className="w-full rounded-2xl shadow-2xl shadow-[#1a6cf5]/10 hover:shadow-[#1a6cf5]/20 transition-all duration-500 hover:scale-[1.02]"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Lab Section */}
      <hr className="section-divider" />
      <section className="py-10 sm:py-16 relative" data-testid="lab-section">
        <div
          ref={labRef}
          className={`max-w-4xl mx-auto px-6 lg:px-8 text-center transition-all duration-700 ${labInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-sm uppercase tracking-[0.2em] text-[#1a6cf5] mb-4 font-medium">{t.lab.label}</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-8 heading-accent">{t.lab.h2}</h2>
          <p className="text-base text-gray-300 leading-relaxed max-w-3xl mx-auto">{t.lab.text}</p>
        </div>
      </section>

      {/* Features */}
      <hr className="section-divider" />
      <section id="features" className="py-12 sm:py-20" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6 heading-accent">{t.features.h2}</h2>
            <p className="text-base text-gray-300 max-w-3xl mx-auto">{t.features.intro}</p>
          </div>
          <div ref={featuresRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.features.cards.map((card, i) => (
              <div
                key={`feature-${card.title}`}
                className={`group bg-[#121b33]/50 backdrop-blur-md border border-white/10 rounded-2xl shadow-lg hover:-translate-y-2 hover:border-[#1a6cf5]/30 hover:shadow-[#1a6cf5]/10 hover:shadow-xl transition-all duration-300 overflow-hidden ${featuresInView ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${i * 0.1}s` }}
                data-testid={`feature-card-${i}`}
              >
                <div className="h-48 overflow-hidden relative">
                  <img src={FEATURE_IMAGES[i]} alt={card.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1f] via-transparent to-transparent opacity-40 group-hover:opacity-60 transition-opacity duration-300" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-medium text-white mb-3">{card.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{card.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsSection t={t} carouselOpts={carouselOpts} carouselPlugins={carouselPlugins} />

      <MetricsSection t={t} />

      {/* CTA */}
      <section className="py-12 sm:py-16" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6 heading-accent">{t.cta.h2}</h2>
          <p className="text-base text-gray-300 mb-10 max-w-2xl mx-auto">{t.cta.text}</p>
          <a
            href="#contact"
            className="bg-[#1a6cf5] text-white rounded-full px-8 py-3 font-medium hover:bg-[#3b82f6] transition-all btn-pulse-glow inline-block"
            data-testid="cta-demo-btn"
          >
            {t.cta.button}
          </a>
        </div>
      </section>

      <FaqSection t={t} />

      {/* Contact */}
      <section id="contact" className="py-12 sm:py-20" data-testid="contact-section">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white text-center mb-10">{t.contact.h2}</h2>
          <ContactForm />
        </div>
      </section>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
    </main>
  );
}
